/**
 * Keeps the header from shifting as you navigate.
 *
 * Some pages scroll the window (product details) and some scroll their own content (every table
 * page), so the window scrollbar comes and goes — and with it 15-odd pixels of width. The header
 * is the one element that survives navigation, so it has to occupy the same width either way:
 * `body.has-window-scrollbar` says which case we are in, and global.scss reserves the difference
 * on the header when the scrollbar is absent.
 *
 * Detected rather than declared per page, so a page that changes how it scrolls — or a new one
 * that never thinks about it — stays correct.
 */
export function trackWindowScrollbar() {
  publishScrollbarWidth();
  update();

  window.addEventListener('resize', update);

  // Fires when the scrollbar appears or disappears: the root element's content box narrows by
  // exactly its width. Catches content that only overflows once its data has loaded.
  new ResizeObserver(update).observe(document.documentElement);
}

let isTrackingSuspended = false;

function update() {
  if (isTrackingSuspended) return;

  const hasScrollbar = window.innerWidth - document.documentElement.clientWidth > 0;
  document.body.classList.toggle('has-window-scrollbar', hasScrollbar);
}

let lockCount = 0;

/**
 * Stops the window scrolling behind an overlay, without the page behind it moving.
 *
 * Reference counted, because overlays stack: a modal opened from inside the article flyout must
 * not re-lock (there is no scrollbar left to measure, so the gutter would read as 0 and the
 * reservation be dropped), and closing it must not unlock the page under the flyout that is
 * still up.
 *
 * Taking the body out of flow is what stops the scroll, and it takes the window scrollbar
 * with it — so everything behind the overlay widens into the gutter the scrollbar leaves.
 * Two boxes have to be held, because they are sized by different things:
 *
 * - The page's own content, through the body, which the width below pins.
 * - The header, which is `position: fixed` and so sized by the viewport rather than by the
 *   body — the width below cannot reach it. It already reserves this exact gutter whenever
 *   the scrollbar reads as absent (see global.scss), so the lock simply puts it in that
 *   state, and does it in the same tick: the observer would notice a frame later, and the
 *   header would be seen to widen before it settled.
 *
 * Measured before the lock, because afterwards there is no scrollbar left to measure.
 */
export function lockWindowScroll() {
  if (lockCount++ > 0) return;

  const gutter = window.innerWidth - document.documentElement.clientWidth;
  // Held so the observer, which fires on the reflow this causes, cannot undo either of the
  // reservations below while the overlay is up.
  isTrackingSuspended = true;

  document.body.style.top = `-${window.scrollY}px`;
  document.body.style.position = 'fixed';

  // Pages that scroll their own content have no window scrollbar to lose, and reserving a
  // gutter there would introduce the very shift this is here to prevent.
  if (gutter > 0) {
    document.body.style.width = `calc(100% - ${gutter}px)`;
    document.body.classList.remove('has-window-scrollbar');
  }
}

export function unlockWindowScroll() {
  if (lockCount === 0 || --lockCount > 0) return;

  const scrollY = parseInt(document.body.style.top, 10) || 0;

  document.body.style.top = '';
  document.body.style.position = 'static';
  document.body.style.width = '';

  isTrackingSuspended = false;
  update();

  window.scrollTo(0, -scrollY);
}

/** Overlay scrollbars (macOS, touch) measure 0 and so reserve nothing. */
function publishScrollbarWidth() {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll;';
  probe.innerHTML = '<div style="height:200px"></div>';
  document.body.appendChild(probe);

  // Measured from rects rather than offsetWidth/clientWidth, which round to whole pixels: this
  // page's root font size scales the layout, so the real scrollbar is a fractional width and a
  // rounded reservation leaves the header a fraction of a pixel out.
  const outer = probe.getBoundingClientRect().width;
  const inner = (probe.firstElementChild as HTMLElement).getBoundingClientRect().width;
  probe.remove();

  document.documentElement.style.setProperty('--scrollbar-width', `${outer - inner}px`);
}
